import { api } from "@/shared/lib/axios/axios.api"
import { API_PREFIX } from "@/shared/lib/axios/axios.constants"
import { streamServerSentEvents } from "@/shared/lib/event-source/fetch-event-source"
import type {
  Review,
  ReviewDetail,
  ReviewStreamEvent,
  ReviewSummary,
  ReviewTarget,
} from "./review.types"

const REVIEWS = `${API_PREFIX}/assistant/reviews`

export const listReviews = async ({
  pageSize,
  offset,
}: {
  pageSize?: number
  offset?: number
}) => {
  const response = await api.get<ReviewSummary[]>(REVIEWS, {
    params: { ...(pageSize && { pageSize }), ...(offset && { offset }) },
  })
  return response.data
}

export const getReview = async ({ reviewId, quiet = false }: { reviewId: string; quiet?: boolean }) => {
  const response = await api.get<ReviewDetail>(`${REVIEWS}/${reviewId}`, {
    suppressServerErrorAlert: quiet,
  })
  return response.data
}

/**
 * Multipart, and the field names are the server's own snake_case.
 *
 * The request interceptor decamelizes plain object bodies but passes FormData through untouched —
 * humps would walk `Object.keys` on it, find nothing, and hand axios an empty object. So these
 * names are written as the server reads them rather than being converted on the way out.
 *
 * **Each language is immediately followed by its file.** The server refuses any other ordering:
 * equal counts in the wrong order would zip cleanly and evaluate a translation under another
 * language's label, quietly.
 *
 * Reference files go after `bilingual_document` and before the first `target_language`, outside
 * the language/file pairing. After the loop, a bilingual document's last `target_language` has no
 * `target_file` behind it, and a reference there sits where a translation would.
 */
export const createReview = async ({
  title,
  sourceLanguage,
  domain,
  sourceFile,
  targets,
  systemPrompt,
  promptTemplateId,
  bilingualDocument = false,
  effort,
  referenceFiles = [],
}: {
  title?: string
  sourceLanguage: string
  domain?: string
  sourceFile: File
  targets: { language: string; file?: File }[]
  bilingualDocument?: boolean
  effort?: import("@/entities/assistant/assistant.types").ReviewEffort
  /** The rubric, with its language placeholders left for the server to fill per translation. */
  systemPrompt?: string
  /** Only ever sent alongside the text it came from; the server refuses it on its own. */
  promptTemplateId?: string
  /** Material every evaluation of the review reads as data. Only send it where
   * `GET /assistant/access` offers `reference_attachments`; the server refuses it otherwise. */
  referenceFiles?: readonly File[]
}) => {
  const form = new FormData()
  if (effort) form.append("effort", effort)
  if (title?.trim()) form.append("title", title.trim())
  form.append("source_language", sourceLanguage)
  if (domain) form.append("domain", domain)
  if (systemPrompt) form.append("system_prompt", systemPrompt)
  if (promptTemplateId) form.append("prompt_template_id", promptTemplateId)
  form.append("source_file", sourceFile, sourceFile.name)
  if (bilingualDocument) form.append("bilingual_document", "true")
  referenceFiles.forEach((file) => form.append("reference_file", file, file.name))
  targets.forEach((target) => {
    form.append("target_language", target.language)
    if (target.file) form.append("target_file", target.file, target.file.name)
  })

  const response = await api.post<Review>(REVIEWS, form)
  return response.data
}

export const deleteReview = async ({ reviewId }: { reviewId: string }) => {
  await api.delete<void>(`${REVIEWS}/${reviewId}`)
}

/**
 * Starts the evaluation.
 *
 * Returns as soon as the server has claimed the target — `202`, the row now `in_progress` — while
 * the model runs in a task of the server's own. It used to block for the minutes the model took,
 * tied to this request, and a browser that went elsewhere cancelled the run. Follow it with
 * `watchTargetEvaluation`.
 */
export const evaluateTarget = async ({
  reviewId,
  targetId,
}: {
  reviewId: string
  targetId: string
}) => {
  const response = await api.post<ReviewTarget>(
    `${REVIEWS}/${reviewId}/targets/${targetId}/evaluate`
  )
  return response.data
}

/**
 * Follows an evaluation as server-sent events, until it is over.
 *
 * A raw `fetch`, not the axios instance — axios cannot stream a body in the browser — so this
 * bypasses the interceptors: the token is attached by hand and nothing renews it, and field names
 * arrive as the server writes them. Callers make an axios request first, as the chat does, and
 * read targets through `fromWire`.
 */
export const watchTargetEvaluation = ({
  reviewId,
  targetId,
  signal,
  onEvent,
}: {
  reviewId: string
  targetId: string
  signal: AbortSignal
  onEvent: (event: ReviewStreamEvent) => void
}) =>
  streamServerSentEvents<ReviewStreamEvent>({
    url: `${import.meta.env.VITE_BASE_API_URL}${REVIEWS}/${reviewId}/targets/${targetId}/stream`,
    method: "GET",
    signal,
    onEvent,
  })
